import { Component, OnInit } from '@angular/core';
import { BaseReportActionsDialog } from '../base-report-actions-dialog';
import { GaValidateService } from '../../../formly/ga-validate.service';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../formly/ga-formly.service';
import { FormattingService } from '../../../services/formatting.service';
import { IPermissionConfig, PermissionsConfigService } from './helpers/permissions-config.service';
import { ReportDataService } from '../../report-data.service';
import * as _ from 'underscore';
import { GaEventService } from '../../../services/ga-event.service';
import { TranslationService } from '../../../translation/translation.service';
import { IDualMultiSelectElement } from '../../../dual-multi-select/dual-multi-select.component';

interface IOption {
  id:boolean;
  description:string;
}

interface IActionDialogParams {
  showAccessType:boolean;
}

@Component({
  selector: 'permissions-action',
  template: `
    <default-dialog [title]="title" [buttons]="buttons" [closeButton]="true" (onClose)="onCancel.emit()">
      <div class="customer-permissions">
        <div class="alert alert-warning" *ngIf="generalError">
          <i class="fa fa-warning"></i>
          {{ generalError }}
        </div>

        <dual-multi-select
          [label]="config.selectLabel"
          [modeRemove]="config.modeRemove"
          [possibleValues]="possibleValues"
          [(currentValues)]="currentValues"
          (onChange)="onChange()"
          [selectDisabled]="selectDisabled"
          [selectLoading]="selectLoading">
        </dual-multi-select>

        <ng-container *ngIf="actionDialogParams?.showAccessType">
          <formly [form]="form" [fields]="fields" [model]="formModel"></formly>
        </ng-container>
      </div>
    </default-dialog>
  `
})
export class PermissionsActionComponent extends BaseReportActionsDialog implements OnInit {
  public formModel:{ useCurrentCRS?:IOption } = {};

  public useCurrentCRSOptions:IOption[] = [
    {
      id: true,
      description: 'Use same list of countries/regions/subcompanies as the ones the user currently has access to'
    },
    {
      id: false,
      description: 'Only give access to the countries/regions/subcompanies according to the generic user\'s access'
    }
  ];

  public possibleValues:IDualMultiSelectElement[] = [];
  public currentValues:IDualMultiSelectElement[] = [];
  public selectLoading:boolean;
  public selectDisabled:boolean;
  public executeButtonDisabled:boolean = true;
  public generalError:string;
  public config:IPermissionConfig;
  public actionDialogParams:IActionDialogParams;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private permissionsConfigService:PermissionsConfigService,
    private reportDataService:ReportDataService,
    protected translationService:TranslationService,
    eventService:GaEventService,
    validateService:GaValidateService
  ) {
    super(eventService, validateService);
  }

  public ngOnInit():void {
    // get configuration
    this.config = this.permissionsConfigService.getConfig(this.action.code);
    this.title = this.translationService.instant(this.config.executeButtonTitle? this.config.executeButtonTitle : this.title);

    // generate dialog buttons
    super.ngOnInit();
    this.updateButtonStatus(undefined, this.executeButtonDisabled);

    // init model
    this.model = {
      [ this.config.model ]: []
    };

    if (this.actionDialogParams?.showAccessType) {
      this.model = _.extend(this.model, {
        useCurrentCRS: true,
        webshops: []
      });

      this.formModel = {
        useCurrentCRS: this.useCurrentCRSOptions[ 0 ]
      };

      // create fields
      this.fields = this.createFields();
    }

    // load report action info
    this.loadReportActionInfo();
  }

  private createFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aRadio('useCurrentCRS', 'Access type')
        .isRequired()
        .withOptions(this.useCurrentCRSOptions)
        .withFormat(this.formattingService.formatLov)
        .withLabelClass('col-md-12 col-space-bottom')
        .withValueClass('col-md-12')
        .withInfo('Specify the access to countries/regions/subcompanies based on current user or generic user')
        .withoutInfoOffset()
        .onChange(() => {
          this.model.useCurrentCRS = this.formModel.useCurrentCRS.id;
        })
        .build()
    ];
  }

  private loadReportActionInfo():void {
    this.selectLoading = true;
    this.reportDataService.loadReportActionInfo(this.reportId, this.action.id, this.rowSelection)
      .then((actionInfoData:any) => {
        if (!actionInfoData.info.length) {
          this.selectDisabled = true;
          this.generalError = this.config.errorMessage;
          this.executeButtonDisabled = true;
          this.updateButtonStatus(undefined, this.executeButtonDisabled);
        }
        this.possibleValues = actionInfoData.info;
      })
      .catch(() => {
        this.selectDisabled = true;
      })
      .finally(() => this.selectLoading = false);
  }

  public onChange():void {
    this.model[ this.config.model ] = this.currentValues;
    this.executeButtonDisabled = !this.currentValues.length;
    this.updateButtonStatus(undefined, this.executeButtonDisabled);
  }
}